/**
 * structuredData.js — the machine-readable half of the page.
 *
 * Three different consumers, one graph:
 *
 *  SEO — classic crawlers want Person/WebSite/BreadcrumbList and clean
 *        canonical + OG tags.
 *  AEO — answer engines (Google AI Overviews, Perplexity, Bing Copilot)
 *        want FAQPage with self-contained answers, and `speakable` marking
 *        which nodes are safe to read aloud.
 *  GEO — generative engines resolve *entities*, not keywords. So every
 *        node gets a stable @id, nodes reference each other by @id, and
 *        the same claims appear in visible text. A model that crawls this
 *        page can reconstruct the whole résumé as a fact graph.
 */

import {
  SITE_URL,
  person,
  summary,
  longSummary,
  allSkills,
  experiences,
  projects,
  education,
  certifications,
  achievements,
  services,
  faqs,
} from "../data/profile";
import pricing from "../data/pricing.json";

const ID = {
  website: `${SITE_URL}/#website`,
  page: `${SITE_URL}/#webpage`,
  person: `${SITE_URL}/#person`,
  faq: `${SITE_URL}/#faq`,
  breadcrumb: `${SITE_URL}/#breadcrumb`,
  work: `${SITE_URL}/#portfolio`,
  place: `${SITE_URL}/#place`,
};

const orgId = (name) =>
  `${SITE_URL}/#org-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

const sameAs = [
  person.social.linkedin,
  person.social.github,
  person.social.instagram,
];

/* ---------------------------------------------------------------- */

function buildPerson() {
  return {
    "@type": "Person",
    "@id": ID.person,
    name: person.name,
    givenName: person.givenName,
    familyName: person.familyName,
    alternateName: person.alternateNames,
    url: SITE_URL,
    image: {
      "@type": "ImageObject",
      "@id": `${SITE_URL}/#headshot`,
      url: `${SITE_URL}/ss3.jpg`,
      caption: `${person.name} — ${person.headline}`,
    },
    jobTitle: person.headline,
    description: summary,
    disambiguatingDescription: longSummary,
    email: `mailto:${person.email}`,
    knowsLanguage: person.languages,
    nationality: { "@type": "Country", name: person.location.country },

    address: {
      "@type": "PostalAddress",
      addressLocality: person.location.city,
      addressRegion: person.location.region,
      addressCountry: person.location.countryCode,
    },
    // GEO: explicit coordinates + service radius so location-qualified
    // queries ("MERN developer in Haryana") can resolve to this entity.
    homeLocation: {
      "@type": "Place",
      "@id": ID.place,
      name: `${person.location.city}, ${person.location.region}, ${person.location.country}`,
      geo: {
        "@type": "GeoCoordinates",
        latitude: person.location.latitude,
        longitude: person.location.longitude,
      },
    },
    workLocation: person.serviceAreas.map((a) => ({
      "@type": "Place",
      name: a,
    })),

    worksFor: {
      "@type": "Organization",
      "@id": orgId(experiences[0].company),
      name: experiences[0].company,
    },

    hasOccupation: {
      "@type": "Occupation",
      name: "Full Stack Developer",
      occupationalCategory: "15-1254.00", // O*NET: Web Developers
      description: longSummary,
      skills: allSkills.join(", "),
      occupationLocation: {
        "@type": "City",
        name: person.location.city,
      },
    },

    knowsAbout: allSkills,

    alumniOf: education
      .filter((e) => e.institution !== "CBSE")
      .map((e) => ({
        "@type": "EducationalOrganization",
        "@id": orgId(e.institution),
        name: e.institution,
      })),

    hasCredential: [
      ...education
        .filter((e) => e.institution !== "CBSE")
        .map((e) => ({
          "@type": "EducationalOccupationalCredential",
          name: e.degree,
          credentialCategory: "degree",
          educationalLevel: e.short === "MCA" ? "Postgraduate" : "Undergraduate",
          recognizedBy: { "@type": "Organization", name: e.institution },
        })),
      ...certifications.map((c) => ({
        "@type": "EducationalOccupationalCredential",
        name: c.title,
        credentialCategory: "certificate",
        recognizedBy: { "@type": "Organization", name: c.issuer },
        ...(c.verifyUrl ? { url: c.verifyUrl } : {}),
      })),
    ],

    award: achievements.map((a) => `${a.title} — ${a.org}`),

    makesOffer: services.map((s) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: s.title,
        description: s.description,
        provider: { "@id": ID.person },
        areaServed: person.serviceAreas.map((a) => ({
          "@type": "Place",
          name: a,
        })),
      },
    })),

    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Hiring & project enquiries",
      email: person.email,
        availableLanguage: person.languages,
      areaServed: "Worldwide",
    },

    sameAs,
  };
}

/** Each role becomes its own OrganizationRole node, so a model can answer
 *  "where did he work in 2024" without parsing prose. */
function buildRoles() {
  return experiences.map((e) => ({
    "@type": "OrganizationRole",
    "@id": `${SITE_URL}/#role-${e.id}`,
    roleName: e.role,
    startDate: e.start,
    ...(e.end ? { endDate: e.end } : {}),
    description: e.summary,
    member: { "@id": ID.person },
    memberOf: {
      "@type": "Organization",
      "@id": orgId(e.company),
      name: e.company,
      address: {
        "@type": "PostalAddress",
        addressRegion: person.location.region,
        addressCountry: person.location.countryCode,
      },
    },
  }));
}

function buildProjects() {
  return {
    "@type": "ItemList",
    "@id": ID.work,
    name: `Selected work by ${person.name}`,
    description: `Production web platforms designed and built by ${person.name}.`,
    numberOfItems: projects.length,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    itemListElement: projects.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#project-${p.id}`,
        name: p.title,
        alternateName: p.tagline,
        applicationCategory: "WebApplication",
        operatingSystem: "Web browser",
        description: `${p.summary} ${p.result}`,
        abstract: p.problem,
        datePublished: p.year,
        ...(p.liveUrl ? { url: p.liveUrl } : {}),
        author: { "@id": ID.person },
        creator: { "@id": ID.person },
        provider: { "@type": "Organization", name: p.client },
        keywords: p.stack.join(", "),
        featureList: p.highlights,
      },
    })),
  };
}

function buildFaq() {
  return {
    "@type": "FAQPage",
    "@id": ID.faq,
    // AEO: tells voice/answer surfaces exactly which text is quotable.
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".aeo-answer", ".aeo-summary"],
    },
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
        author: { "@id": ID.person },
      },
    })),
  };
}

/**
 * Builds the whole @graph for the homepage.
 * `extra` lets sub-pages (blog index, blog post) append their own nodes.
 */
export function buildGraph({ url = SITE_URL, extra = [] } = {}) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": ID.website,
        url: SITE_URL,
        name: `${person.name} — Portfolio`,
        alternateName: ["Soham Dang Portfolio", "SD Portfolio"],
        description: summary,
        inLanguage: "en",
        publisher: { "@id": ID.person },
        copyrightHolder: { "@id": ID.person },
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE_URL}/blogs?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },

      // ProfilePage is the correct type for a personal site and is what
      // Google's "about this person" surfaces key off.
      {
        "@type": "ProfilePage",
        "@id": ID.page,
        url,
        name: `${person.name} | ${person.headline}`,
        description: summary,
        isPartOf: { "@id": ID.website },
        about: { "@id": ID.person },
        mainEntity: { "@id": ID.person },
        inLanguage: "en",
        primaryImageOfPage: { "@id": `${SITE_URL}/#headshot` },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [".aeo-summary", ".aeo-answer"],
        },
        breadcrumb: { "@id": ID.breadcrumb },
      },

      {
        "@type": "BreadcrumbList",
        "@id": ID.breadcrumb,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          {
            "@type": "ListItem",
            position: 2,
            name: "Portfolio",
            item: `${SITE_URL}/#projects`,
          },
        ],
      },

      buildPerson(),
      ...buildRoles(),
      buildProjects(),
      buildFaq(),
      ...extra,
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Per-page graphs
 *
 * Every page reuses the SAME Person node by @id rather than restating it.
 * That is the whole point of an entity graph: ten pages referencing one
 * @id is one well-evidenced entity, whereas ten copies of a Person node
 * is ten weakly-related ones.
 * ------------------------------------------------------------------ */

/** Minimal Person stub — pages other than the homepage reference, not repeat. */
function personRef() {
  return { "@id": ID.person };
}

export function breadcrumbNode(trail, pageUrl) {
  return {
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: trail.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE_URL}${c.url === "/" ? "/" : c.url}`,
    })),
  };
}

export function faqNode(faqs, pageUrl) {
  if (!faqs || !faqs.length) return null;
  return {
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".aeo-answer", ".aeo-summary"],
    },
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a, author: personRef() },
    })),
  };
}

/**
 * Graph for a landing page.
 *
 * `hire`/`service` pages additionally emit a Service node, because that is
 * genuinely what they describe. Informational pages do not — a Service node
 * on an explainer would be schema spam.
 */
export function buildLandingGraph(page, { trail }) {
  const url = `${SITE_URL}/${page.slug}`;
  const nodes = [
    {
      "@type": "WebSite",
      "@id": ID.website,
      url: SITE_URL,
      name: `${person.name} — Portfolio`,
      inLanguage: "en",
      publisher: personRef(),
    },
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: page.meta.title,
      description: page.meta.description,
      isPartOf: { "@id": ID.website },
      about: personRef(),
      inLanguage: "en",
      breadcrumb: { "@id": `${url}#breadcrumb` },
      primaryImageOfPage: { "@id": `${SITE_URL}/#headshot` },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".aeo-summary", ".aeo-answer"],
      },
    },
    breadcrumbNode(trail, url),
    // The full Person node lives on the homepage; every other page points
    // at it. One canonical definition, many references.
    buildPerson(),
  ];

  if (page.kind === "hire" || page.kind === "service" || page.kind === "hub") {
    nodes.push({
      "@type": "Service",
      "@id": `${url}#service`,
      name: page.h1,
      description: page.lead,
      serviceType: page.meta.title.split("|")[0].trim(),
      provider: personRef(),
      areaServed: person.serviceAreas.map((a) => ({ "@type": "Place", name: a })),
      mainEntityOfPage: { "@id": `${url}#webpage` },
      // An Offer is emitted only where a real price is published. Marking
      // up a price on a page that doesn't state one is a mismatch between
      // the markup and the page, which is the one thing that gets
      // structured data ignored outright.
      ...(page.showPricing
        ? {
            offers: {
              "@type": "Offer",
              price: pricing.perSeat.amount,
              priceCurrency: pricing.perSeat.currency,
              description: pricing.summary,
              availability: "https://schema.org/InStock",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: pricing.perSeat.amount,
                priceCurrency: pricing.perSeat.currency,
                unitText: pricing.perSeat.unit,
                referenceQuantity: {
                  "@type": "QuantitativeValue",
                  value: 1,
                  unitCode: "MON",
                  unitText: pricing.perSeat.period,
                },
              },
            },
          }
        : {}),
    });
  }

  const faq = faqNode(page.faqs, url);
  if (faq) nodes.push(faq);

  return { "@context": "https://schema.org", "@graph": nodes };
}

/** Graph for a single project case-study page. */
export function buildProjectGraph(project, { trail }) {
  const url = `${SITE_URL}/projects/${project.id}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": ID.website,
        url: SITE_URL,
        name: `${person.name} — Portfolio`,
        inLanguage: "en",
        publisher: personRef(),
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: `${project.title} — ${project.tagline}`,
        description: project.summary,
        isPartOf: { "@id": ID.website },
        about: { "@id": `${SITE_URL}/#project-${project.id}` },
        breadcrumb: { "@id": `${url}#breadcrumb` },
        inLanguage: "en",
      },
      breadcrumbNode(trail, url),
      {
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#project-${project.id}`,
        name: project.title,
        alternateName: project.tagline,
        applicationCategory: "WebApplication",
        operatingSystem: "Web browser",
        description: `${project.summary} ${project.result || ""}`.trim(),
        abstract: project.problem || undefined,
        datePublished: project.year || undefined,
        url: project.liveUrl || url,
        image: project.image ? `${SITE_URL}${project.image}` : undefined,
        author: personRef(),
        creator: personRef(),
        keywords: (project.stack || []).join(", "),
        featureList: project.highlights,
        mainEntityOfPage: { "@id": `${url}#webpage` },
      },
      buildPerson(),
    ],
  };
}

/**
 * Graph for a supporting page (/projects, /blogs, /contact).
 *
 * Deliberately NOT the homepage graph. Re-emitting ProfilePage and FAQPage
 * on every route would duplicate the FAQ across the site — which is exactly
 * the FAQ-schema spam Google penalises — and give four pages a claim to
 * being the profile page. One ProfilePage, one FAQPage, referenced Person.
 */
export function buildPageGraph({
  path,
  name,
  description,
  type = "WebPage",
  trail = [{ name: "Home", url: "/" }],
  items = null,
}) {
  const url = `${SITE_URL}${path}`;
  const nodes = [
    {
      "@type": "WebSite",
      "@id": ID.website,
      url: SITE_URL,
      name: `${person.name} — Portfolio`,
      inLanguage: "en",
      publisher: { "@id": ID.person },
    },
    {
      "@type": type,
      "@id": `${url}#webpage`,
      url,
      name,
      description,
      isPartOf: { "@id": ID.website },
      about: { "@id": ID.person },
      inLanguage: "en",
      breadcrumb: { "@id": `${url}#breadcrumb` },
      ...(items ? { mainEntity: { "@id": `${url}#list` } } : {}),
    },
    breadcrumbNode(trail, url),
    buildPerson(),
  ];

  if (items) {
    nodes.push({
      "@type": "ItemList",
      "@id": `${url}#list`,
      numberOfItems: items.length,
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/projects/${it.id}`,
        name: it.title,
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": nodes };
}
